import { Formik, FormikProps } from "formik";
import { isEmpty } from "lodash-es";
import React, { FC, memo, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Anchor } from "@/components/Anchor";
import { Button } from "@/components/Button";
import { ErrorText } from "@/components/errors";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { TLegalDocumentType } from "@/data/legal-documents";
import { Flex, Img } from "@/eworkitComponents";
import { FormikCheckbox } from "@/formikComponents";
import { RouteButton } from "@/infrastructure/Route";
import { AnimatedLayout } from "@/views/components/Layout/components/AnimatedLayout";
import { AnimatedLayoutContainer } from "@/views/components/Layout/components/AnimatedLayoutContainer";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";

import agreementImage from "./agreement-icon.svg";
import { createAgreementValidatorSchema } from "./createAgreementValidatiorSchema";
import { createFormInitialValues } from "./createFormInitialValues";
import { useLegalDocumentsGuard } from "./useLegalDocumentsGuard";

import "./_agreement-guard.scss";

export interface IAgreementGuardProps {}

export const AgreementGuard: FC<IAgreementGuardProps> = memo(({ children }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["session", "common"]);
  const { hasRequiredLegalDocumentsToAccept, requiredLegalDocuments, acceptLegalDocuments } = useLegalDocumentsGuard();

  const handleSubmit = useCallback(async (values: Record<TLegalDocumentType, boolean>) => {
    try {
      await acceptLegalDocuments(Object.keys(values) as TLegalDocumentType[]);

      addToast({
        text: t("LEGAL_DOCUMENTS_UPDATED.UPDATED_MESSAGE"),
        type: "success",
      });
    } catch (error) {
      addToast({
        text: t("common:VALIDATION_MESSAGES.GENERAL"),
        type: "warning",
      });
    }
  }, []);

  if (!hasRequiredLegalDocumentsToAccept) {
    return <>{children}</>;
  }

  const validatorSchema = createAgreementValidatorSchema(requiredLegalDocuments);
  const initialValues = createFormInitialValues(requiredLegalDocuments);

  return (
    <AnimatedLayout>
      <AnimatedLayoutContainer size="md">
        <Header weight="bold">{t("LEGAL_DOCUMENTS_UPDATED.HEADER")}</Header>

        <Flex margin={["lg", false]} justify="center">
          <Img src={agreementImage} className="agreement-guard__icon" />
        </Flex>

        <Formik
          initialValues={initialValues}
          validationSchema={validatorSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}>
          {(form: FormikProps<Record<TLegalDocumentType, boolean>>) => (
            <Flex direction="column" innerMargin="md">
              <Text>{t("LEGAL_DOCUMENTS_UPDATED.MESSAGE")}</Text>

              <Flex direction="column" innerMargin="xs">
                {Object.keys(form.values).map((documentType: TLegalDocumentType) => (
                  <FormikCheckbox
                    key={documentType}
                    name={documentType}
                    required
                    checked={form.values[documentType]}
                    onChange={(value: boolean) => form.setFieldValue(documentType, value)}>
                    <Trans ns="session" i18nKey={`LEGAL_DOCUMENTS_UPDATED.CHECKBOX.${documentType}`}>
                      <Anchor href={`${router.get("legal-information").path}?type=${documentType}`} target="_blank" />
                    </Trans>
                  </FormikCheckbox>
                ))}

                {!isEmpty(form.errors) && <ErrorText error={t("LEGAL_DOCUMENTS_UPDATED.ERROR_MESSAGE")} />}

                <Flex justify="flex-end" innerMargin="sm">
                  <RouteButton variant="secondary" size="lg" path={router.get("logout").path} testName="logout">
                    <Text size="md">{t("LEGAL_DOCUMENTS_UPDATED.BUTTON_LOGOUT")}</Text>
                  </RouteButton>
                  <Button type="submit" size="lg" onClick={form.submitForm} disabled={form.isSubmitting}>
                    {t("LEGAL_DOCUMENTS_UPDATED.BUTTON_CONTINUE")}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Formik>
      </AnimatedLayoutContainer>
    </AnimatedLayout>
  );
});
